// Dev-only fixtures for the confirm-page auto check-in block, mounted by
// dev-preview.vue under /library/components. Label prefixes mark the provenance:
//   MOCK      — a salabam/airobot-mocks JSON file, verbatim
//   NON MOCK  — legal under airobot-mocks/types.ts, missing there (gaps.md §2)
//   IDENTITÀ  — the vendor's passengers[] passed through on the leg, joined to a
//               boarding pass on passenger_id: every degradation of that join
//   DEGENERE  — the type permits it, the component props do not: fallback probe

const OUTBOUND_KEY = 'MXP-BCN-VY-20260901';
const RETURN_KEY = 'BCN-MXP-VY-20260908';
const OUTBOUND_REQUEST = 'req_a11b22c33d';
const RETURN_REQUEST = 'req_e44f55a66b';

// Vendor shape: `name` is the given name as a plain string, `last_name` the
// surname, `external_id` the Amadeus travelers[].id.
// Casing is deliberately mixed across these: Amadeus returns uppercase, but the
// FE renders whatever arrives, so the block has to look right either way.
const PASSENGER_ONE = {
  id: 1,
  name: 'LUIGI',
  last_name: 'FERRARO',
  external_id: '1',
};

const PASSENGER_TWO = {
  id: 2,
  name: 'Sofia',
  last_name: 'Colombo',
  external_id: '2',
};

const PASSENGER_THREE = {
  id: 3,
  name: 'Anna',
  last_name: 'DE LUCA',
  external_id: '3',
};

const boarding_pass = (overrides = {}) => ({
  format: 'pdf',
  passenger_journey_id: 6101,
  available: true,
  seat_number: '11A',
  gate_close_time: '2026-09-01T07:05:00Z',
  public_url: `https://public.instago.click/bp/${OUTBOUND_REQUEST}/6101`,
  ...overrides,
});

const flight = (overrides = {}) => ({
  itinerary_id: '1',
  itinerary_key: OUTBOUND_KEY,
  request_id: OUTBOUND_REQUEST,
  status: 'pending',
  last_event_at: '2026-09-01T05:10:00Z',
  error: null,
  boarding_passes: [],
  events: [],
  ...overrides,
});

const return_flight = (overrides = {}) =>
  flight({
    itinerary_id: '2',
    itinerary_key: RETURN_KEY,
    request_id: RETURN_REQUEST,
    last_event_at: '2026-09-08T05:20:00Z',
    ...overrides,
  });

const checkin = (status_summary, flights = []) => ({
  confirmcode: 'BLF3P8W1ZK7',
  flight_voucher_id: 884977,
  integrated_order_id: 'IO-2026-884977',
  order_id: 884977,
  amadeus_order_id: 'eJzTd9f3NDQxNjM2sTQ1NLYwMDU1sjBP',
  carrier_reference: 'K8M2QR',
  source_reference: 'QLZ7PW',
  status_summary,
  flights,
  audit_trail: [],
});

export const CHECK_IN_SCENARIOS = [
  {
    key: 'not-requested',
    label: 'MOCK — not_requested — nessuna riga di richiesta',
    probe: 'status_summary: not_requested, flights: []',
    expectation:
      'In produzione non arriva mai: upstream.js:339 scarta il blocco quando il summary è not_requested. Qui passa dal ramo flights vuoti: una sola riga "Servizio non richiesto", nessun link.',
    checkin: checkin('not_requested', []),
  },
  {
    key: 'pending-one-way',
    label: 'MOCK — pending — sola andata in attesa della finestra',
    probe: '1 leg pending, 0 boarding pass',
    expectation:
      'Una riga "Richiesta in attesa" senza etichetta itinerario (stato omogeneo) e senza link.',
    checkin: checkin('pending', [
      flight({
        itinerary_key: 'FCO-CTA-FR-20260812',
        request_id: 'req_9f1c2a7b4d',
        last_event_at: '2026-08-10T09:15:00Z',
      }),
    ]),
  },
  {
    key: 'success-one-way',
    label: 'MOCK — success — sola andata, 1 pax, carta pronta',
    probe: '1 leg success, 1 boarding pass con public_url',
    expectation:
      '"Richiesta emessa" + un link. Nessuna intestazione itinerario perché ce n\'è uno solo. Il nome mostrato è posizionale ("P1"): boarding_passes non porta il nominativo.',
    checkin: checkin('success', [
      flight({
        itinerary_key: 'FCO-CTA-FR-20260812',
        request_id: 'req_9f1c2a7b4d',
        status: 'success',
        last_event_at: '2026-08-12T04:35:00Z',
        boarding_passes: [
          boarding_pass({
            passenger_journey_id: 5501,
            seat_number: '14C',
            gate_close_time: '2026-08-12T06:30:00Z',
            public_url: 'https://public.instago.click/bp/req_9f1c2a7b4d/5501',
          }),
        ],
      }),
    ]),
  },
  {
    key: 'success-round-trip',
    label: 'MOCK — success — andata/ritorno, 2 leg × 2 pax',
    probe: '2 leg success omogenei, 4 boarding pass (l\'ultima con public_url null)',
    expectation:
      'Stato omogeneo: una sola riga "Richiesta emessa" e le carte dei due itinerari unite sotto le rispettive intestazioni. La carta 6202 ha public_url null → href="null", link morto (gaps.md §1.2).',
    checkin: checkin('success', [
      flight({
        status: 'success',
        boarding_passes: [
          boarding_pass(),
          boarding_pass({
            passenger_journey_id: 6102,
            seat_number: '11B',
            public_url: `https://public.instago.click/bp/${OUTBOUND_REQUEST}/6102`,
          }),
        ],
      }),
      return_flight({
        status: 'success',
        boarding_passes: [
          boarding_pass({
            passenger_journey_id: 6201,
            seat_number: '9C',
            gate_close_time: '2026-09-08T07:15:00Z',
            public_url: `https://public.instago.click/bp/${RETURN_REQUEST}/6201`,
          }),
          boarding_pass({
            passenger_journey_id: 6202,
            seat_number: '9D',
            gate_close_time: '2026-09-08T07:15:00Z',
            public_url: null,
          }),
        ],
      }),
    ]),
  },
  {
    key: 'partial-success-round-trip',
    label: 'MOCK — partial_success — andata emessa, ritorno in attesa',
    probe: 'leg success + leg pending',
    expectation:
      'Esito normale di un andata/ritorno. Due righe distinte con etichetta itinerario, il link solo sotto l\'andata. Il valore partial_success non viene mai reso: il componente ragiona per leg.',
    checkin: checkin('partial_success', [
      flight({
        status: 'success',
        boarding_passes: [boarding_pass()],
      }),
      return_flight(),
    ]),
  },
  {
    key: 'failed-vendor-rejected',
    label: 'MOCK — failed — vendor rifiuta (solo message, nessun code)',
    probe: '1 leg failed, error.message senza error.code',
    expectation:
      '"Richiesta fallita" + copy generica con email assistenza. Il motivo ("documento scaduto") viene scartato: nessuna prop lo trasporta (gaps.md §1.3).',
    checkin: checkin('failed', [
      flight({
        itinerary_key: 'NAP-STN-FR-20260715',
        request_id: 'req_b77c88d99e',
        status: 'failed',
        last_event_at: '2026-07-13T22:41:00Z',
        error: {
          message:
            'Airobot vendor rejected checkin: passenger travel document expired',
        },
      }),
    ]),
  },
  {
    key: 'failed-polling-timeout',
    label: 'MOCK — failed — polling esaurito (polling_max_errors)',
    probe: '1 leg failed, error con code + message',
    expectation:
      'Identico al caso precedente: forma di error diversa, resa uguale. Un errore tecnico transitorio e un rifiuto documenti non si distinguono.',
    checkin: checkin('failed', [
      flight({
        itinerary_key: 'NAP-STN-FR-20260715',
        request_id: 'req_b77c88d99e',
        status: 'failed',
        last_event_at: '2026-07-15T05:55:00Z',
        error: {
          code: 'polling_max_errors',
          message: 'Polling failed 5 times: upstream 503 from vendor',
        },
      }),
    ]),
  },
  {
    key: 'canceled-after-resync',
    label: 'MOCK — partial_success — leg annullato da un resync di orario',
    probe: 'leg canceled + leg success',
    expectation:
      'Il leg canceled prende la copy di failed ("Richiesta fallita" + assistenza): stessa voce di POSSIBLE_STATUS_INFO. Da valutare se un annullamento da cambio orario meriti un messaggio proprio.',
    checkin: checkin('partial_success', [
      flight({
        status: 'canceled',
        last_event_at: '2026-08-25T12:00:00Z',
      }),
      return_flight({
        status: 'success',
        boarding_passes: [
          boarding_pass({
            passenger_journey_id: 6201,
            seat_number: '9C',
            gate_close_time: '2026-09-08T07:15:00Z',
            public_url: `https://public.instago.click/bp/${RETURN_REQUEST}/6201`,
          }),
        ],
      }),
    ]),
  },
  {
    key: 'accepted-transient',
    label: 'NON MOCK — accepted — stato transitorio della state machine',
    probe: 'leg status: accepted (roll-up pending)',
    expectation:
      'accepted non è in POSSIBLE_STATUS_INFO e cade sul default pending: coerente con il roll-up di airobot-be, quindi il fallback qui è quello giusto.',
    checkin: checkin('pending', [flight({ status: 'accepted' })]),
  },
  {
    key: 'success-boarding-pass-not-ready',
    label: 'NON MOCK — success — carta non ancora scaricabile',
    probe: 'leg success, boarding pass available:false, public_url:null',
    expectation:
      'La copy dice "scaricale qui sotto" ma il link punta a null. Il componente non filtra su available (gaps.md §1.4 + §2.1).',
    checkin: checkin('success', [
      flight({
        status: 'success',
        boarding_passes: [
          boarding_pass({
            available: false,
            seat_number: null,
            gate_close_time: null,
            public_url: null,
          }),
        ],
      }),
    ]),
  },
  {
    key: 'success-public-url-null',
    label: 'NON MOCK — success — available:true ma public_url null',
    probe: 'boarding pass disponibile solo dietro proxy autenticato',
    expectation:
      'available:true segnala che la carta esiste lato vendor, ma senza public_url serve il proxy lf_checkinBoardingPass.php. Oggi esce href="null": manca il fallback e le prop non portano request_id/passenger_journey_id/format.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        boarding_passes: [boarding_pass({ public_url: null })],
      }),
    ]),
  },
  {
    key: 'connecting-flight',
    label: 'NON MOCK — success — volo con scalo, 2 journey per pax',
    probe: '1 itinerario, 4 boarding pass (2 pax × 2 tratte)',
    expectation:
      'Un solo itinerario con quattro carte. Le etichette posizionali diventano P1..P4 mentre i passeggeri sono due: le carte della seconda tratta risultano di persone inesistenti (gaps.md §2.2).',
    checkin: checkin('success', [
      flight({
        itinerary_key: 'MXP-FRA-LH-20260901',
        boarding_passes: [
          boarding_pass({ passenger_journey_id: 7101, seat_number: '12A' }),
          boarding_pass({ passenger_journey_id: 7102, seat_number: '12B' }),
          boarding_pass({
            passenger_journey_id: 7201,
            seat_number: '21C',
            gate_close_time: '2026-09-01T11:40:00Z',
          }),
          boarding_pass({
            passenger_journey_id: 7202,
            seat_number: '21D',
            gate_close_time: '2026-09-01T11:40:00Z',
          }),
        ],
        status: 'success',
      }),
    ]),
  },
  {
    key: 'mixed-pax-single-leg',
    label: 'NON MOCK — success — un pax imbarcato, l\'altro senza carta',
    probe: '1 leg success, 1 boarding pass su 2 pax',
    expectation:
      'Il leg è success ma una sola carta è presente. Nulla segnala che manca il secondo passeggero (gaps.md §2.3).',
    checkin: checkin('success', [
      flight({
        status: 'success',
        boarding_passes: [boarding_pass()],
      }),
    ]),
  },
  {
    key: 'partial-success-with-failure',
    label: 'NON MOCK — partial_success — andata emessa, ritorno fallito',
    probe: 'leg success + leg failed',
    expectation:
      'Caso peggiore del misto: la riga fallita deve restare visibile accanto al link della riuscita. Se collassasse su una sola riga il fallimento sparirebbe.',
    checkin: checkin('partial_success', [
      flight({
        status: 'success',
        boarding_passes: [boarding_pass()],
      }),
      return_flight({
        status: 'failed',
        error: {
          code: 'polling_max_errors',
          message: 'Polling failed 5 times: upstream 503 from vendor',
        },
      }),
    ]),
  },
  {
    key: 'all-legs-canceled',
    label: 'NON MOCK — partial_success — tutti i leg annullati',
    probe: '2 leg canceled (roll-up partial_success per il quirk di _derive_summary)',
    expectation:
      'Stato omogeneo: collassa in una riga "Richiesta fallita" senza etichetta itinerario. Nonostante il summary dica partial_success, non c\'è nulla di riuscito.',
    checkin: checkin('partial_success', [
      flight({ status: 'canceled' }),
      return_flight({ status: 'canceled' }),
    ]),
  },
  {
    key: 'failed-round-trip',
    label: 'NON MOCK — failed — entrambi i leg falliti',
    probe: '2 leg failed con error diversi',
    expectation:
      'Omogeneo: una riga sola. I due motivi diversi (documento vs polling) collassano nella stessa copy generica.',
    checkin: checkin('failed', [
      flight({
        status: 'failed',
        error: { message: 'Airobot vendor rejected checkin: name mismatch' },
      }),
      return_flight({
        status: 'failed',
        error: {
          code: 'polling_max_errors',
          message: 'Polling failed 5 times: upstream 503 from vendor',
        },
      }),
    ]),
  },
  {
    key: 'multi-city-three-legs',
    label: 'NON MOCK — success — multi-city, 3 leg omogenei',
    probe: '3 leg success, 1 boarding pass ciascuno',
    expectation:
      'Verifica il collasso omogeneo su più di due tratte: una riga di stato e tre gruppi di carte, uno per itinerario.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        boarding_passes: [boarding_pass()],
      }),
      flight({
        itinerary_id: '2',
        itinerary_key: 'BCN-LIS-VY-20260905',
        request_id: 'req_c99d00e11f',
        status: 'success',
        boarding_passes: [
          boarding_pass({ passenger_journey_id: 6301, seat_number: '4F' }),
        ],
      }),
      flight({
        itinerary_id: '3',
        itinerary_key: 'LIS-MXP-TP-20260910',
        request_id: 'req_d22e33f44a',
        status: 'success',
        boarding_passes: [
          boarding_pass({ passenger_journey_id: 6401, seat_number: '18A' }),
        ],
      }),
    ]),
  },
  {
    key: 'null-itinerary-key',
    label: 'NON MOCK — itinerary_key null su un leg (connettore senza key)',
    probe: 'leg success con itinerary_key null + leg pending con key valorizzata',
    expectation:
      'Stato non omogeneo: la riga senza key mostra "()" vuote e il gruppo di carte perde l\'intestazione. Il ramo null di itinerary_label esiste, ma il markup della parentesi non è condizionato.',
    checkin: checkin('partial_success', [
      flight({
        itinerary_key: null,
        status: 'success',
        boarding_passes: [boarding_pass()],
      }),
      return_flight(),
    ]),
  },
  {
    key: 'unknown-format',
    label: 'NON MOCK — format "unknown" + posto e gate null',
    probe: 'boarding pass con format fuori dall\'enum del tipo',
    expectation:
      'airobot-be usa "unknown" come default quando il vendor non manda il formato (gaps.md §2.7). Il componente non legge format, quindi rende normalmente: nessuna regressione, solo un tipo da allineare.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        boarding_passes: [
          boarding_pass({
            format: 'unknown',
            seat_number: null,
            gate_close_time: null,
          }),
        ],
      }),
    ]),
  },
  {
    key: 'identity-mixed-casing',
    label: 'IDENTITÀ — maiuscolo, capitalizzato e cognome composto',
    probe: '3 pax: "LUIGI FERRARO", "Sofia Colombo", "Anna DE LUCA"',
    expectation:
      'Il vendor rimanda quello che gli è arrivato: Amadeus scrive in maiuscolo, altre sorgenti no. Le tre etichette stanno nella stessa riga per giudicare se serve normalizzare la resa o lasciarla verbatim.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        passengers: [PASSENGER_ONE, PASSENGER_TWO, PASSENGER_THREE],
        boarding_passes: [
          boarding_pass({ passenger_id: 1 }),
          boarding_pass({
            passenger_id: 2,
            passenger_journey_id: 6102,
            seat_number: '11B',
          }),
          boarding_pass({
            passenger_id: 3,
            passenger_journey_id: 6103,
            seat_number: '11C',
          }),
        ],
      }),
    ]),
  },
  {
    key: 'identity-passthrough-two-pax',
    label: 'IDENTITÀ — passengers passthrough, 2 pax',
    probe: 'flights[].passengers [{id:1},{id:2}] + carte con passenger_id 1 e 2',
    expectation:
      'Il caso buono: le carte rendono "LUIGI FERRARO" e "Sofia Colombo". Il join è passenger_id → passengers[].id, e i nomi arrivano dal vendor senza che airobot-be li rimappi.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        passengers: [PASSENGER_ONE, PASSENGER_TWO],
        boarding_passes: [
          boarding_pass({ passenger_id: 1 }),
          boarding_pass({
            passenger_id: 2,
            passenger_journey_id: 6102,
            seat_number: '11B',
          }),
        ],
      }),
    ]),
  },
  {
    key: 'identity-passengers-absent',
    label: 'IDENTITÀ — passengers assente, carte con passenger_id',
    probe: 'nessun passengers[] sul leg',
    expectation:
      'Senza la lista non c\'è nulla da unire: si torna alle etichette posizionali "P1" e "P2". È lo stato di oggi in produzione e il fallback deve restare, non nascondere i link.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        boarding_passes: [
          boarding_pass({ passenger_id: 1 }),
          boarding_pass({
            passenger_id: 2,
            passenger_journey_id: 6102,
            seat_number: '11B',
          }),
        ],
      }),
    ]),
  },
  {
    key: 'identity-unmatched-passenger-id',
    label: 'IDENTITÀ — passenger_id che non esiste in passengers',
    probe: 'carta con passenger_id 9, passengers con id 1 e 2',
    expectation:
      'Join a vuoto → "P1". Copre un passthrough parziale (lista troncata o rigenerata) senza rendere etichette di un\'altra persona.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        passengers: [PASSENGER_ONE, PASSENGER_TWO],
        boarding_passes: [boarding_pass({ passenger_id: 9 })],
      }),
    ]),
  },
  {
    key: 'identity-no-passenger-id',
    label: 'IDENTITÀ — passengers presente ma carte senza passenger_id',
    probe: 'passengers valorizzato, nessuna chiave di join sulla carta',
    expectation:
      'Senza passenger_id non si indovina per posizione: "P1" e "P2". Accoppiare per indice sarebbe sbagliato, il vendor non garantisce lo stesso ordine delle due liste.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        passengers: [PASSENGER_ONE, PASSENGER_TWO],
        boarding_passes: [
          boarding_pass(),
          boarding_pass({ passenger_journey_id: 6102, seat_number: '11B' }),
        ],
      }),
    ]),
  },
  {
    key: 'identity-create-shape-journey-key',
    label: 'IDENTITÀ — passengers in forma create (passenger_journey_id)',
    probe: 'passengers senza id utile ma con passenger_journey_id',
    expectation:
      'La risposta di create porta passenger_journey_id dentro passengers[], la webhook no. Con questa forma il join passa dalla chiave journey e rende comunque "LUIGI FERRARO".',
    checkin: checkin('success', [
      flight({
        status: 'success',
        passengers: [
          {
            id: 77,
            name: 'LUIGI',
            last_name: 'FERRARO',
            external_id: '1',
            passenger_journey_id: 6101,
          },
        ],
        boarding_passes: [boarding_pass()],
      }),
    ]),
  },
  {
    key: 'identity-partial-name',
    label: 'IDENTITÀ — passenger con solo last_name',
    probe: 'passengers[0] senza name',
    expectation:
      'traveler_name pretende firstname E lastname: con solo il cognome ricade su "P1", il cognome non viene mostrato. Da valutare se un nome parziale meriti comunque di essere reso.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        passengers: [{ id: 1, last_name: 'FERRARO', external_id: '1' }],
        boarding_passes: [boarding_pass({ passenger_id: 1 })],
      }),
    ]),
  },
  {
    key: 'identity-connecting-flight',
    label: 'IDENTITÀ — volo con scalo, 1 pax e 2 journey',
    probe: '2 carte con lo stesso passenger_id 1',
    expectation:
      'Le due tratte condividono il passeggero: entrambe rendono "LUIGI FERRARO". Senza il join uscivano "P1" e "P2", cioè una persona inventata per la seconda tratta.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        itinerary_key: 'MXP-FRA-LH-20260901',
        passengers: [PASSENGER_ONE],
        boarding_passes: [
          boarding_pass({ passenger_id: 1, passenger_journey_id: 7101 }),
          boarding_pass({
            passenger_id: 1,
            passenger_journey_id: 7201,
            seat_number: '21C',
            gate_close_time: '2026-09-01T11:40:00Z',
          }),
        ],
      }),
    ]),
  },
  {
    key: 'identity-mixed-round-trip',
    label: 'IDENTITÀ — identità solo sull\'andata',
    probe: 'andata con passengers, ritorno senza',
    expectation:
      'Andata "LUIGI FERRARO"/"Sofia Colombo", ritorno "P1"/"P2". Il passthrough è per leg, quindi le due tratte possono degradare in modo diverso nella stessa prenotazione.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        passengers: [PASSENGER_ONE, PASSENGER_TWO],
        boarding_passes: [
          boarding_pass({ passenger_id: 1 }),
          boarding_pass({
            passenger_id: 2,
            passenger_journey_id: 6102,
            seat_number: '11B',
          }),
        ],
      }),
      return_flight({
        status: 'success',
        boarding_passes: [
          boarding_pass({
            passenger_journey_id: 6201,
            seat_number: '9C',
            gate_close_time: '2026-09-08T07:15:00Z',
            public_url: `https://public.instago.click/bp/${RETURN_REQUEST}/6201`,
          }),
          boarding_pass({
            passenger_journey_id: 6202,
            seat_number: '9D',
            gate_close_time: '2026-09-08T07:15:00Z',
            public_url: `https://public.instago.click/bp/${RETURN_REQUEST}/6202`,
          }),
        ],
      }),
    ]),
  },
  {
    key: 'unknown-leg-status',
    label: 'DEGENERE — stato leg non previsto ("waiting-documents")',
    probe: 'leg status fuori dall\'enum',
    expectation:
      'Cade su pending. Copre il caso in cui airobot-be smettesse di normalizzare a monte: la resa resta sensata, ma "servono i tuoi documenti" non si può dire.',
    checkin: checkin('pending', [flight({ status: 'waiting-documents' })]),
  },
  {
    key: 'success-summary-no-flights',
    label: 'DEGENERE — success con flights vuoti',
    probe: 'status_summary success, flights: []',
    expectation:
      'Passa dal leg sintetico costruito sul summary: "Richiesta emessa" senza nessun link, perché boarding_passes è forzato a [].',
    checkin: checkin('success', []),
  },
  {
    key: 'partial-success-no-flights',
    label: 'DEGENERE — partial_success con flights vuoti',
    probe: 'status_summary partial_success, flights: []',
    expectation:
      'partial_success non è in POSSIBLE_STATUS_INFO: il leg sintetico cade su pending e dice "Richiesta in attesa" per uno stato che è invece parzialmente concluso.',
    checkin: checkin('partial_success', []),
  },
  {
    key: 'null-summary',
    label: 'DEGENERE — status_summary null con flights valorizzati',
    probe: 'status_summary: null (ammesso da types.ts, non dalla prop)',
    expectation:
      'La prop status_summary è String+required: atteso un warning Vue in console. Con flights valorizzati la resa non ne dipende, quindi il warning è l\'unico effetto.',
    checkin: checkin(null, [
      flight({
        status: 'success',
        boarding_passes: [boarding_pass()],
      }),
    ]),
  },
  {
    key: 'null-summary-no-flights',
    label: 'DEGENERE — status_summary null e flights vuoti',
    probe: 'payload completamente vuoto di informazione',
    expectation:
      'Peggior combinazione: warning sulla prop e leg sintetico su null → fallback pending. Rende "Richiesta in attesa" senza avere alcun dato.',
    checkin: checkin(null, []),
  },
];
